import { FC, ReactNode, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { ContentAdSlot } from '@/ads';
import { PlayerProfilePageHero } from '@/components/PlayerProfilePageHero/PlayerProfilePageHero';
import {
  CountryFlag,
  Dropdown,
  DropdownOption,
  PlayerMatchStat,
  TabGroup,
  TeamInfo,
  Typography,
} from '@dreamteam-monorepo/ui';
import { Loader2 } from 'lucide-react';
import { useFetchPlayer } from '@/hooks/useFetchPlayer';
import { useFetchPlayerMatches } from '@/hooks/useFetchPlayerMatches';
import { useCurrentTournamentCalendar } from '@/hooks/useCurrentTournamentCalendar';
import { useLeaderboardPeriods } from '@/hooks/useLeaderboardPeriods';
import type { PlayerMatchHistoryItem } from '@/lib/network';
import { PlayerProfilePageFixture } from '@/components/PlayerProfilePageFixture/PlayerProfilePageFixture';
import { trackFantasyEvent } from '@/analytics/fantasyEventTracking';
import { isMatchDetailSupportedCompetition } from '@/lib/competitionLabels';
import { groupMatchesByPeriod } from './groupMatchesByPeriod';

export interface PlayerStats {
  label: string;
  value: number;
}

export interface PlayerMatchDetail {
  matchId?: string;
  competitionLabel: string;
  competitionIcon?: ReactNode;
  matchDate?: Date;
  matchVenue?: string;
  mdLabel?: string;
  mdPoints: string;
  leftTeam: TeamInfo;
  rightTeam: TeamInfo;
  status?: string;
  statusVariant?: 'finished' | 'live';
  stats: PlayerMatchStat[];
  kickoffAt?: string;
  venue?: string;
  periodId?: string;
  matchDetailAvailable?: boolean;
}

const formatStatValue = (value: number): string => String(Number(value.toFixed(1)));

const PlayerProfile: FC = () => {
  type Tab = 'results' | 'fixtures';

  const { playerId } = useParams<{ playerId: string }>();

  const { data: activeTournament } = useCurrentTournamentCalendar();
  const tournamentCalendarId = activeTournament?.id ?? null;
  const { data: periodsData } = useLeaderboardPeriods(tournamentCalendarId, true);

  const periodType = periodsData?.type ?? 'gameweek';
  const periodLabel = periodType === 'matchday' ? 'Matchday' : 'Gameweek';

  const { data: matchesResponse } = useFetchPlayerMatches(
    playerId,
    periodType === 'gameweek' ? 'gameweek' : 'matchday'
  );

  const {
    data: player,
    isLoading,
    isError,
  } = useFetchPlayer({
    playerId,
    periodType: periodType === 'gameweek' ? 'gameweek' : 'matchday',
    fullFixtures: true,
  });

  const dropdownOptions: DropdownOption[] = useMemo(() => {
    return [
      { id: 'all', label: `All ${periodLabel}s`, value: 'all' },
      ...(periodsData?.periods ?? []).map(p => ({
        id: p.id,
        label: `${periodLabel} ${p.number}`,
        value: p.id,
      })),
    ];
  }, [periodsData, periodLabel]);

  const [activeTab, setActiveTab] = useState<Tab>('results');
  const [selectedPeriod, setSelectedPeriod] = useState<string[]>(['all']);

  const handlePeriodChange = (option: DropdownOption, checked: boolean) => {
    trackFantasyEvent('playerProfileFilter', {
      filter_name: periodLabel,
      filter_value: `${checked ? 'Select' : 'Remove'} ${option.label}`,
    });
    setSelectedPeriod(prev => {
      if (option.value === 'all' || prev.includes('all')) {
        return checked && option.value === 'all' ? ['all'] : [option.value];
      }
      const newSelection = checked ? [...prev, option.value] : prev.filter(v => v !== option.value);
      return newSelection.length === 0 ? ['all'] : newSelection;
    });
  };

  const playerStats: PlayerStats[] = useMemo(() => {
    if (!player) return [];
    return [
      {
        label: periodType === 'gameweek' ? 'GW Pts' : 'MD Pts',
        value:
          periodType === 'gameweek' ? (player.gameweekPoints ?? 0) : (player.matchdayPoints ?? 0),
      },
      { label: 'Avg. Pts', value: player.averagePoints },
      { label: 'Bonus Pts', value: player.bonusPoints },
      { label: 'Total Pts', value: player.totalPoints },
      { label: 'Games Pld', value: player.gamesPlayed ?? 0 },
      { label: 'Last 3 Avg. Pts', value: player.last3Average },
      { label: 'Best Perform', value: player.bestPerformance ?? 0 },
    ];
  }, [periodType, player]);

  const nextFixture = player?.nextFixtures?.[0];

  const resultsMatches: PlayerMatchDetail[] = useMemo(() => {
    const playerMatchItems = matchesResponse?.data?.items ?? [];

    return playerMatchItems.map((item: PlayerMatchHistoryItem) => ({
      matchId: item.matchId,
      competitionLabel: item.competitionLabel,
      competitionIcon: undefined as ReactNode | undefined,
      periodId: item.periodId,
      mdLabel: item.mdLabel,
      mdPoints: item.mdPoints,
      status: item.status,
      statusVariant: item.statusVariant,
      kickoffAt: item.kickoffAt,
      venue: item.venue,
      matchDetailAvailable:
        item.matchDetailAvailable || isMatchDetailSupportedCompetition(item.competitionLabel),
      leftTeam: {
        name: item.leftTeam.name,
        logo: item.leftTeam.flagKey ? (
          <CountryFlag country={item.leftTeam.flagKey} className="h-full w-full object-cover" />
        ) : (
          <div className="size-6 bg-gray-500 rounded-full" />
        ),
        score: item.leftTeam.score ?? undefined,
      },
      rightTeam: {
        name: item.rightTeam.name,
        logo: item.rightTeam.flagKey ? (
          <CountryFlag country={item.rightTeam.flagKey} className="h-full w-full object-cover" />
        ) : (
          <div className="size-6 bg-gray-500 rounded-full" />
        ),
        score: item.rightTeam.score ?? undefined,
      },
      stats: item.stats.map(stat => ({
        label: stat.label,
        total: stat.total,
        points: stat.points,
      })),
    }));
  }, [matchesResponse?.data?.items]);

  const displayedMatches = useMemo(() => {
    if (activeTab === 'results') {
      if (selectedPeriod.includes('all')) {
        return resultsMatches;
      }
      return resultsMatches.filter(m => selectedPeriod.includes(m.periodId ?? ''));
    }
    // Fixtures tab uses player.nextFixtures
    const fixtures = player?.nextFixtures ?? [];
    return fixtures.map((fixture): PlayerMatchDetail => {
      const isPlayerHome = fixture.isHome;
      const playerTeam = {
        name: player?.contestantName ?? '',
        logo: player?.contestantFlagKey ? (
          <CountryFlag country={player.contestantFlagKey} className="h-full w-full object-cover" />
        ) : (
          <div className="size-6 bg-gray-500 rounded-full" />
        ),
        score: undefined,
      };
      const opponentTeam = {
        name: fixture.opponentShortName || fixture.opponentName || '',
        logo: fixture.contestantFlagKey ? (
          <CountryFlag country={fixture.contestantFlagKey} className="h-full w-full object-cover" />
        ) : (
          <div className="size-6 bg-gray-500 rounded-full" />
        ),
        score: undefined,
      };
      const periodNumber = fixture.gameweek ?? fixture.matchday ?? 0;
      const mdLabel = periodNumber
        ? `${periodType === 'matchday' ? 'MD' : 'GW'} ${periodNumber}`
        : '';
      return {
        matchId: fixture.fixtureId,
        competitionLabel: fixture.competitionName ?? '',
        competitionIcon: undefined,
        mdLabel,
        mdPoints: '—',
        leftTeam: isPlayerHome ? playerTeam : opponentTeam,
        rightTeam: isPlayerHome ? opponentTeam : playerTeam,
        status: undefined,
        statusVariant: undefined,
        kickoffAt: fixture.kickoffAt,
        venue: fixture.venue ?? '',
        matchDetailAvailable:
          (fixture.matchDetailAvailable ?? false) ||
          isMatchDetailSupportedCompetition(fixture.competitionName),
        stats: [],
      };
    });
  }, [activeTab, selectedPeriod, resultsMatches, player, periodType]);

  const groupedMatches = useMemo(() => groupMatchesByPeriod(displayedMatches), [displayedMatches]);

  if (isLoading) {
    return (
      <div className="components light flex flex-col gap-[--padding-lg]">
        <PlayerProfilePageHero isLoading />
        <div
          role="status"
          aria-live="polite"
          className="flex h-64 flex-col items-center justify-center gap-[--padding-sm] bg-white px-[--padding-md] text-[--button-contained-blue-base-bg]"
        >
          <Loader2 height={32} width={32} className="animate-spin" />
          <Typography variant="paragraph020" className="text-[--color-neutral-700]">
            Loading player profile...
          </Typography>
        </div>
      </div>
    );
  }

  if (isError || !player) {
    return (
      <div className="components light flex flex-col gap-[--padding-lg]">
        <PlayerProfilePageHero />
        <div className="flex items-center justify-center h-64">
          <Typography variant="paragraph020" className="text-[--color-neutral-600]">
            Failed to load player profile. Please try again later.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="components light flex flex-col gap-[--padding-lg]">
      <PlayerProfilePageHero
        firstName={player.firstName}
        lastName={player.lastName}
        position={player.position}
        price={player.price}
        priceChangeUp={player.priceChangeUp}
        priceChangeDown={player.priceChangeDown}
        teamName={player.contestantName}
        teamShortName={player.teamName}
        shirtKey={player.shirtKey}
        contestantFlagKey={player.contestantFlagKey}
      />
      <div className="flex flex-col gap-[--padding-lg] max-w-[300px] md:max-w-[600px] lg:max-w-[846px] mx-auto w-full">
        {/* Stats Row */}
        <div className="hidden lg:flex gap-4 justify-between items-center">
          {playerStats.map((stat, index) => (
            <div key={index} className="w-[110px] flex flex-col gap-1">
              <Typography variant="paragraph010" className="text-center">
                {stat.label}
              </Typography>
              <Typography variant="subheadline040" className="text-center">
                {formatStatValue(stat.value)}
              </Typography>
            </div>
          ))}
        </div>
        <div className="flex lg:hidden mx-auto">
          <div className="flex flex-col rounded-tl-lg rounded-bl-lg border border-[--page-header-bg-grey]">
            <div className="flex h-1/2">
              {playerStats.slice(0, 4).map((stat, index) => (
                <div
                  key={index}
                  className="w-[50px] flex flex-col gap-1  items-center justify-center"
                >
                  <Typography
                    variant="paragraph010"
                    className="text-center text-[8px] leading-[8px] lg:text-[12px] lg:leading-[16px]"
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="subheadline040"
                    className="text-center text-[14px] leading-[16px] lg:text-[18px] lg:leading-[24px]"
                  >
                    {formatStatValue(stat.value)}
                  </Typography>
                </div>
              ))}
            </div>
            <div className="flex h-1/2 border-t border-[--page-header-bg-grey]">
              {playerStats.slice(4).map((stat, index) => (
                <div
                  key={index}
                  className="w-[70px] flex flex-col gap-1 items-center justify-center"
                >
                  <Typography
                    variant="paragraph010"
                    className="text-center text-[8px] leading-[8px] lg:text-[12px] lg:leading-[16px]"
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="subheadline040"
                    className="text-center text-[14px] leading-[16px] lg:text-[18px] lg:leading-[24px]"
                  >
                    {formatStatValue(stat.value)}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col overflow-hidden rounded-tr-lg rounded-br-lg border border-[--page-header-bg-grey]">
            <div className="flex items-center bg-[--page-header-bg-grey] px-3 py-2">
              <Typography
                variant="paragraph010"
                className="text-[10px] leading-[10px] lg:text-[12px] lg:leading-[16px]"
              >
                Next Fixture:
              </Typography>
            </div>
            <div className="flex flex-col items-center gap-3 px-3 py-2">
              <div className="size-8 lg:size-6 overflow-hidden rounded-full">
                {nextFixture?.contestantFlagKey && (
                  <CountryFlag
                    country={nextFixture.contestantFlagKey}
                    className="h-full w-full object-cover"
                  />
                )}
              </div>
              <Typography
                variant="paragraph010"
                className="text-[10px] leading-[10px] lg:text-[12px] lg:leading-[16px] font-bold"
              >
                {nextFixture?.opponentShortName || nextFixture?.opponentName || '—'}
              </Typography>
            </div>
          </div>
        </div>

        {/* Tabs + Next Fixture + Dropdown */}
        <div className="flex flex-col gap-[--padding-lg]">
          <div className="flex justify-between items-center">
            <TabGroup
              tabs={[
                { id: 'results', label: 'Results' },
                { id: 'fixtures', label: 'Fixtures' },
              ]}
              selectedId={activeTab}
              onChangeTab={id => {
                const tab = id as Tab;
                trackFantasyEvent('playerProfileDataToggle', {
                  toggle_name: tab === 'results' ? 'Results' : 'Fixtures',
                });
                setActiveTab(tab);
              }}
            />
            <div className="hidden lg:flex overflow-hidden rounded-lg border border-[--page-header-bg-grey]">
              <div className="flex items-center bg-[--page-header-bg-grey] px-3 py-2">
                <Typography variant="paragraph010">Next Fixture:</Typography>
              </div>
              <div className="flex items-center gap-3 px-3 py-2">
                <Typography variant="paragraph010" className="font-bold">
                  {nextFixture?.opponentShortName || nextFixture?.opponentName || '—'}
                </Typography>
                <div className="size-6 overflow-hidden rounded-full">
                  {nextFixture?.contestantFlagKey && (
                    <CountryFlag
                      country={nextFixture.contestantFlagKey}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <Dropdown
            label={periodType === 'gameweek' ? 'Gameweek' : 'Matchday'}
            options={dropdownOptions}
            values={selectedPeriod}
            multiple
            onWhite
            onValueChange={handlePeriodChange}
            className="flex-1 max-w-[150px] md:w-40 md:flex-none"
          />
        </div>

        {/* Match List */}
        <div className="flex flex-col gap-6">
          {displayedMatches.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <Typography variant="paragraph020" className="text-[--color-neutral-600]">
                No {activeTab} found for selected filters
              </Typography>
            </div>
          ) : (
            groupedMatches.map(([gwLabel, matches]) => (
              <div key={gwLabel} className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <Typography
                    variant="subheadline030"
                    className="text-[16px] leading-[20px] lg:text-[20px] lf:leading-6 font-bold text-[--color-neutral-900]"
                  >
                    {gwLabel}
                  </Typography>
                </div>
                <div className="flex flex-col gap-4">
                  {matches.map((match, index) => (
                    <PlayerProfilePageFixture matchDetail={match} key={index} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <ContentAdSlot id="ad-boa-leaderboard" />
      </div>
    </div>
  );
};

export default PlayerProfile;
